import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  ArrowLeft,
  Upload,
  Trash2,
  Loader2,
  Sparkles,
  FileSpreadsheet,
  FileImage,
  FileText,
  Search,
  CheckCircle2,
  AlertCircle,
  Camera,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { suppliersApi } from '../lib/suppliersApi';
import { useT } from '../lib/i18n';

const ACCEPTED = '.xlsx,.xls,.csv,.pdf,.jpg,.jpeg,.png,.webp';

const STR = {
  fr: {
    load_error:        'Erreur de chargement',
    file_too_large:    'Fichier trop lourd (max 10 Mo)',
    imported:          (n) => `${n} prix importés ✓`,
    import_failed:     "Échec de l'import",
    confirm_delete:    (desc) => `Supprimer "${desc}" ?`,
    deleted:           'Supprimé',
    delete_failed:     'Suppression échouée',
    supplier_default:  'Fournisseur',
    paywall_msg:       'Cette fonctionnalité est réservée aux abonnés payants.',
    view_plans:        'Voir les plans',
    my_suppliers:      'Mes fournisseurs',
    not_found:         'Fournisseur introuvable',
    supplier_offline:  'Fournisseur (hors ligne)',
    import_title:      'Importer la liste de prix',
    import_desc_pre:   'Excel (.xlsx, .xls), CSV, PDF ou photo prise au téléphone. ',
    import_desc_emph:  "L'IA extrait tous tes prix automatiquement.",
    analyzing:         'Analyse…',
    choose_file:       'Choisir un fichier',
    take_photo:        'Prendre une photo',
    file_max_replace:  "· Max 10 Mo · Remplace l'ancienne liste",
    items_saved:       (saved, extracted) => ({ saved, extracted, label_saved: 'prix importés', label_of: 'sur', label_extracted: 'détectés.' }),
    count_prices:      (n) => `${n} prix`,
    search_placeholder: 'Rechercher…',
    empty_prices_a:    'Aucun prix encore importé.',
    empty_prices_b:    'Upload un fichier pour commencer.',
    no_results_for:    (q) => `Aucun résultat pour "${q}"`,
  },
  en: {
    load_error:        'Loading error',
    file_too_large:    'File too large (max 10 MB)',
    imported:          (n) => `${n} prices imported ✓`,
    import_failed:     'Import failed',
    confirm_delete:    (desc) => `Delete "${desc}"?`,
    deleted:           'Deleted',
    delete_failed:     'Deletion failed',
    supplier_default:  'Supplier',
    paywall_msg:       'This feature is reserved for paid subscribers.',
    view_plans:        'View plans',
    my_suppliers:      'My suppliers',
    not_found:         'Supplier not found',
    supplier_offline:  'Supplier (offline)',
    import_title:      'Import price list',
    import_desc_pre:   'Excel (.xlsx, .xls), CSV, PDF or phone photo. ',
    import_desc_emph:  'AI extracts all your prices automatically.',
    analyzing:         'Analyzing…',
    choose_file:       'Choose a file',
    take_photo:        'Take a photo',
    file_max_replace:  '· Max 10 MB · Replaces the previous list',
    items_saved:       (saved, extracted) => ({ saved, extracted, label_saved: 'prices imported', label_of: 'of', label_extracted: 'detected.' }),
    count_prices:      (n) => `${n} ${n === 1 ? 'price' : 'prices'}`,
    search_placeholder: 'Search…',
    empty_prices_a:    'No prices imported yet.',
    empty_prices_b:    'Upload a file to get started.',
    no_results_for:    (q) => `No results for "${q}"`,
  },
};

// Offline cache helpers — last successful supplier+prices per id.
const supplierCacheKey = (id) => `sp.supplier.cache:${id}`;

function readSupplierCache(id) {
  try {
    const raw = localStorage.getItem(supplierCacheKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeSupplierCache(id, data) {
  try { localStorage.setItem(supplierCacheKey(id), JSON.stringify(data)); }
  catch { /* quota / disabled — ignore */ }
}

function formatCurrency(n, currency = 'CAD') {
  try {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n} ${currency}`;
  }
}

export default function SupplierDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, lang } = useT();
  const T = STR[lang === 'en' ? 'en' : 'fr'];
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [prices, setPrices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [search, setSearch] = useState('');
  const [paywall, setPaywall] = useState(false);
  const [isOffline, setIsOffline] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    // 🌐 Offline-first: charger directement le cache, aucun toast d'erreur.
    if (!navigator.onLine) {
      const cached = readSupplierCache(id);
      if (cached) {
        setSupplier(cached.supplier || null);
        setPrices(cached.prices || []);
      } else {
        // Pas de cache pour ce fournisseur, mais on reste sur la page
        // plutôt que de rediriger (mieux UX hors-ligne).
        setSupplier(null);
        setPrices([]);
      }
      setPaywall(false);
      setIsOffline(true);
      setLoading(false);
      return;
    }
    try {
      const list = await suppliersApi.list();
      const s = (list.suppliers || []).find((x) => x.id === id);
      if (!s) {
        toast.error(T.not_found);
        navigate('/suppliers');
        return;
      }
      setSupplier(s);
      const p = await suppliersApi.listPrices(id);
      setPrices(p.prices || []);
      setPaywall(false);
      setIsOffline(false);
      writeSupplierCache(id, { supplier: s, prices: p.prices || [] });
    } catch (err) {
      if (err.status === 402) {
        setPaywall(true);
      } else {
        // Fallback offline: cache local plutôt que toast d'erreur.
        const cached = readSupplierCache(id);
        if (cached) {
          setSupplier(cached.supplier || null);
          setPrices(cached.prices || []);
          setIsOffline(true);
        } else {
          toast.error(err.message || T.load_error);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate, T.not_found, T.load_error]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(T.file_too_large);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await suppliersApi.uploadPriceList(id, file, true);
      setUploadResult(result);
      toast.success(T.imported(result.items_saved));
      // Refresh prices
      const p = await suppliersApi.listPrices(id);
      setPrices(p.prices || []);
    } catch (err) {
      toast.error(err.message || T.import_failed);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDeletePrice = async (price) => {
    if (!window.confirm(T.confirm_delete(price.description))) return;
    try {
      await suppliersApi.removePrice(price.id);
      setPrices((arr) => arr.filter((x) => x.id !== price.id));
      toast.success(T.deleted);
    } catch (err) {
      toast.error(err.message || T.delete_failed);
    }
  };

  const filtered = search.trim()
    ? prices.filter((p) => {
        const q = search.toLowerCase();
        return (
          (p.description || '').toLowerCase().includes(q) ||
          (p.sku || '').toLowerCase().includes(q)
        );
      })
    : prices;

  if (loading) {
    return (
      <Layout title={T.supplier_default}>
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7A1F2B' }} />
        </div>
      </Layout>
    );
  }

  if (paywall) {
    return (
      <Layout title={T.supplier_default}>
        <div className="text-center py-8">
          <p className="mb-4">{T.paywall_msg}</p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-5 py-2.5 rounded-xl font-semibold text-white"
            style={{ backgroundColor: '#7A1F2B' }}
          >
            {T.view_plans}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={supplier?.name || T.supplier_default}>
      <button
        onClick={() => navigate('/suppliers')}
        className="inline-flex items-center gap-1.5 text-sm font-semibold mb-4"
        style={{ color: '#7A1F2B' }}
        data-testid="back-to-suppliers"
      >
        <ArrowLeft size={16} /> {T.my_suppliers}
      </button>

      {isOffline && (
        <div
          className="mb-4 p-3 rounded-xl flex items-center gap-2 text-sm"
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E' }}
          data-testid="supplier-detail-offline-banner"
        >
          <WifiOff className="w-4 h-4 flex-shrink-0" />
          <span>{t('common.offline_cached')}</span>
        </div>
      )}

      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tighter mb-1" style={{ color: '#5A1620' }}>
          {supplier?.name || (isOffline ? T.supplier_offline : '—')}
        </h1>
        {supplier?.category && (
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {supplier.category}
          </p>
        )}
      </div>

      {/* Upload zone */}
      <div
        className="card p-5 mb-6"
        style={{ borderColor: '#C9A961' }}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: '#7A1F2B', color: '#C9A961' }}
          >
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-lg mb-1" style={{ color: '#5A1620' }}>
              {T.import_title}
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#4A5568' }}>
              {T.import_desc_pre}
              <strong>{T.import_desc_emph}</strong>
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid="price-list-file-input"
        />

        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="py-3 px-3 rounded-xl font-semibold text-white text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#7A1F2B' }}
            data-testid="upload-file-btn"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> {T.analyzing}
              </>
            ) : (
              <>
                <Upload size={16} /> {T.choose_file}
              </>
            )}
          </button>
          <button
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.setAttribute('capture', 'environment');
                fileInputRef.current.click();
                // Reset capture after click
                setTimeout(() => fileInputRef.current?.removeAttribute('capture'), 100);
              }
            }}
            disabled={uploading}
            className="py-3 px-3 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 border-2 disabled:opacity-50"
            style={{ borderColor: '#7A1F2B', color: '#7A1F2B', backgroundColor: '#FFF' }}
            data-testid="photo-capture-btn"
          >
            <Camera size={16} /> {T.take_photo}
          </button>
        </div>

        <div className="flex flex-wrap gap-3 text-xs" style={{ color: '#6B7280' }}>
          <span className="inline-flex items-center gap-1">
            <FileSpreadsheet size={12} /> Excel/CSV
          </span>
          <span className="inline-flex items-center gap-1">
            <FileText size={12} /> PDF
          </span>
          <span className="inline-flex items-center gap-1">
            <FileImage size={12} /> {lang === 'en' ? 'Photo' : 'Photo'}
          </span>
          <span>{T.file_max_replace}</span>
        </div>

        {uploadResult && (
          <div
            className="mt-4 p-3 rounded-lg text-sm flex items-start gap-2"
            style={{ backgroundColor: '#DCFCE7', color: '#166534' }}
            data-testid="upload-result"
          >
            <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
            <div>
              <strong>{uploadResult.items_saved} {T.items_saved(uploadResult.items_saved, uploadResult.items_extracted).label_saved}</strong>{' '}
              {T.items_saved(uploadResult.items_saved, uploadResult.items_extracted).label_of}{' '}
              {uploadResult.items_extracted} {T.items_saved(uploadResult.items_saved, uploadResult.items_extracted).label_extracted}
            </div>
          </div>
        )}
      </div>

      {/* Prices table */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-black tracking-tighter" style={{ color: '#5A1620' }}>
          {T.count_prices(prices.length)}
        </h2>
        {prices.length > 0 && (
          <div className="relative flex-1 max-w-xs">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: '#9CA3AF' }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={T.search_placeholder}
              className="w-full pl-9 pr-3 py-2 rounded-lg border-2 outline-none text-sm"
              style={{ borderColor: '#E5DCC9' }}
              data-testid="prices-search"
            />
          </div>
        )}
      </div>

      {prices.length === 0 ? (
        <div className="card p-8 text-center" data-testid="empty-prices">
          <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: '#C9A961' }} />
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {T.empty_prices_a}<br />
            {T.empty_prices_b}
          </p>
        </div>
      ) : (
        <div className="card divide-y" style={{ borderColor: '#E5DCC9' }}>
          {filtered.map((p) => (
            <div
              key={p.id}
              className="p-3 flex items-center gap-3"
              data-testid={`price-row-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate" style={{ color: '#5A1620' }}>
                  {p.description}
                </div>
                <div className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                  {p.sku && <span>SKU: {p.sku} · </span>}
                  {p.unit}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-base" style={{ color: '#5A1620' }}>
                  {formatCurrency(p.unit_price, p.currency)}
                </div>
                <div className="text-xs" style={{ color: '#6B7280' }}>
                  / {p.unit}
                </div>
              </div>
              <button
                onClick={() => onDeletePrice(p)}
                className="p-2 text-red-700 hover:bg-red-50 rounded-lg shrink-0"
                data-testid={`price-delete-${p.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {filtered.length === 0 && search && (
            <div className="p-6 text-center text-sm" style={{ color: '#6B7280' }}>
              {T.no_results_for(search)}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
